import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { SnackbarService } from '@core/services/snackbar.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IChannel, ICheckChannelResponse } from '@pages/creators/creators.interface';
import { AddPlatformInputStore } from '@shared/components/add-platform-input/add-platform-input.store';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SocialBadgeListComponent } from '@shared/components/social-badge-list/social-badge-list.component';
import { IBadgeData } from '@shared/components/social-badge-list/social-badge-list.interface';
import { EPlatform } from '@shared/enums/campaign.enum';
import { EPermissions } from '@shared/enums/permissions.enum';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  imports: [
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    DotsSpinnerComponent,
    ReactiveFormsModule,
    MatButtonModule,
    IconComponent,
    MatErrorComponent,
    SocialBadgeListComponent,
    HasPermissionDirective,
    NgxSkeletonLoaderModule,
  ],
  providers: [AddPlatformInputStore],
  selector: 'app-add-platform-input',
  templateUrl: './add-platform-input.component.html',
})
export class AddPlatformInputComponent {
  public readonly creatorId = input<string | null>(null);
  public readonly createChannelIndividually = input<boolean>(false);
  public readonly createInstantly = input<boolean>(true);
  public readonly errorMessage = input<string | null>(null);
  public readonly placeholder = input<string | null>(null);

  public readonly validateChannelLoading = output<boolean>();
  public readonly validatedChannelResponse = output<ICheckChannelResponse | null>();
  public readonly validatedChannelUrl = output<string | null>();
  public readonly createdChannel = output<IChannel | null>();

  public readonly createMode = signal<boolean>(false);
  public readonly creatingCreator = signal<boolean>(false);

  public readonly EPermissions = EPermissions;

  public readonly form = new FormGroup({
    url: new FormControl<string | null>(null),
  });

  public readonly allowedPlatforms = computed<IBadgeData[]>(() => {
    const platforms = this.store.validPlatforms$();

    if (!platforms) {
      return [];
    }

    return platforms.map((platform) => ({
      id: platform,
      platform: platform as EPlatform,
      platformCreatorId: platform,
    }));
  });

  public readonly store = inject(AddPlatformInputStore);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  private urlToValidate: string | null = null;

  constructor() {
    this.subscribeToUrlChanges();
    this.createErrorEffect();
    this.createValidateChannelEffect();
    this.createCreateChannelEffect();
  }

  public onOpenErrorCreatorClick(): void {
    const creatorId = this.store.errorCreatorId$();

    if (creatorId) {
      WindowRedirectService.openUrlInNewTab(`/creators/${creatorId}`);
    }
  }

  private handleUrlValueChange(url: string | null): void {
    if (!url) {
      this.store.patchState({
        error: null,
        errorCreatorExists: false,
        errorCreatorId: null,
        validatedChannelIcon: null,
        validatedChannelName: null,
        validatedChannelPlatform: null,
        validatedChannelResponse: null,
      });
      this.urlToValidate = null;

      return;
    }

    this.urlToValidate = url;
    this.validateChannelLoading.emit(true);
    this.store.validateChannel({ url });
  }

  private subscribeToUrlChanges(): void {
    this.form
      .get('url')
      ?.valueChanges.pipe(distinctUntilChanged(), debounceTime(1000))
      .subscribe((url) => this.handleUrlValueChange(url));
  }

  private createErrorEffect(): void {
    effect(() => {
      const storeError = this.store.error$();
      const externalError = this.errorMessage();
      const error = storeError || externalError;

      if (error) {
        this.form.get('url')?.markAsTouched();
        this.form.get('url')?.setErrors({ error: true });

        if (!storeError && externalError) {
          this.store.patchState({
            validatedChannelIcon: null,
            validatedChannelName: null,
            validatedChannelPlatform: null,
            validatedChannelResponse: null,
          });
        }
      } else {
        this.form.get('url')?.setErrors(null);
      }
    });
  }

  private createValidateChannelEffect(): void {
    effect(() => {
      if (this.store.validateChannelLoading$()) {
        return;
      }

      if (this.store.error$() || !this.urlToValidate || !this.store.validatedChannelResponse$()) {
        this.validatedChannelResponse.emit(null);
        this.validatedChannelUrl.emit(null);
        this.validateChannelLoading.emit(false);

        return;
      }

      this.validatedChannelResponse.emit(this.store.validatedChannelResponse$());
      this.validatedChannelUrl.emit(this.urlToValidate);
      this.validateChannelLoading.emit(false);

      if (this.createChannelIndividually() && this.creatorId() && this.createInstantly()) {
        this.store.createChannel({
          channel: { url: this.urlToValidate },
          creatorId: this.creatorId()!,
        });
      }
    });
  }

  private createCreateChannelEffect(): void {
    effect(() => {
      if (this.store.createChannelSuccess$()) {
        this.createdChannel.emit(this.store.createdChannel$());
        this.snackbarService.openSuccess(
          this.translate.instant('components.addPlatformInput.platformAddedSuccessfully'),
        );

        this.urlToValidate = null;

        this.form.reset();
        this.store.patchState({
          error: null,
          errorCreatorExists: false,
          errorCreatorId: null,
          validatedChannelIcon: null,
          validatedChannelName: null,
          validatedChannelPlatform: null,
          validatedChannelResponse: null,
        });
      }
    });
  }
}
